import { Toaster as SonnerToaster, toast } from "sonner";
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, CloudOff, Info, RotateCw, Sparkles, XCircle, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy, useMicrocopy } from "../lib/microcopy";
import { Button } from "./button";

export { toast };

export function Toaster({ dir = "ltr" }: { dir?: "ltr" | "rtl" }) {
  const locale = dir === "rtl" ? "ar" : "en";
  return (
    <SonnerToaster
      dir={dir}
      position={dir === "rtl" ? "bottom-left" : "bottom-right"}
      richColors={false}
      closeButton
      containerAriaLabel={microcopy("toastRegion", locale)}
      icons={{
        success: <CheckCircle2 className="size-4 text-aegreen-700" />,
        error: <XCircle className="size-4 text-aered-700" />,
        warning: <AlertTriangle className="size-4 text-camel-700" />,
        info: <Info className="size-4 text-techblue-700" />,
      }}
      toastOptions={{
        closeButtonAriaLabel: microcopy("closeToast", locale),
        classNames: {
          toast: "!rounded-lg !border !border-aeblack-100 !bg-white !text-foreground !shadow-float !font-sans",
          title: "!text-sm !font-semibold !text-strong",
          description: "!text-xs !text-muted-foreground",
          actionButton: "!bg-aegold-600 !text-white !rounded-md !px-3 !font-medium",
          cancelButton: "!bg-aeblack-100 !text-aeblack-800 !rounded-md !px-3 !font-medium",
          closeButton: "!border-hairline !bg-white/90 !text-subtle",
        },
      }}
    />
  );
}

/**
 * The UAE Design System 3.0 callout (`aegov-alert`, the Design System's former "alert"): 4px corners, a soft fill in the palette's 50
 * swatch, text and icon in its 700, a semibold title. Information is Tech Blue, success AE Green, warning Camel, danger AE Red; `ai`
 * marks AI output.
 */
export function InlineAlert({ tone = "info", title, children, className }: { tone?: "info" | "success" | "warning" | "danger" | "ai"; title?: ReactNode; children?: ReactNode; className?: string }) {
  const map = {
    info: { cls: "bg-techblue-50 text-techblue-700", Icon: Info },
    success: { cls: "bg-aegreen-50 text-aegreen-700", Icon: CheckCircle2 },
    warning: { cls: "bg-camel-50 text-camel-700", Icon: AlertTriangle },
    danger: { cls: "bg-aered-50 text-aered-700", Icon: XCircle },
    ai: { cls: "ai-edge bg-aegold-50 text-aeblack-800 [&>svg]:text-aegold-700", Icon: Sparkles },
  }[tone];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex items-start gap-3 rounded-sm px-4 py-3 text-sm", map.cls, className)}>
      <map.Icon className="mt-px size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={title ? "mt-0.5 text-[13px]" : ""}>{children}</div> : null}
      </div>
    </div>
  );
}

/**
 * A calm, full-width failure state (the service is unreachable, the request failed): what happened, what the person can do, and a
 * retry button when `onRetry` is given. Used in place of a page's content, never as a toast.
 */
export function ErrorState({ title, description, onRetry, retrying, icon: Icon = CloudOff, detail, className, children }: {
  title: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  icon?: LucideIcon;
  /** Technical detail (status code, correlation id) shown small underneath. */
  detail?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const m = useMicrocopy();
  return (
    <div role="alert" className={cn("glass-panel flex flex-col items-center justify-center rounded-xl px-6 py-12 text-center", className)}>
      <span className="mb-4 grid size-14 place-items-center rounded-xl bg-aeblack-50 text-aeblack-600"><Icon className="size-7" aria-hidden /></span>
      <h3 className="text-base font-semibold text-strong">{title}</h3>
      {description ? <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p> : null}
      {children ? <div className="mt-4 w-full max-w-md">{children}</div> : null}
      {onRetry ? (
        <Button variant="outline" className="mt-5" onClick={onRetry} loading={retrying}>
          {retrying ? null : <RotateCw />} {m("tryAgain")}
        </Button>
      ) : null}
      {detail ? <p className="mt-4 text-[11px] text-subtle" dir="ltr">{detail}</p> : null}
    </div>
  );
}
